import api from "@/api";
import dayjs from "dayjs";

const defaultEnd = () => {
  //default to a month
  return dayjs()
    .add(1, "month")
    .format("YYYY-MM-DD");
};

const defaultState = {
  title: "",
  end_date: defaultEnd(),
  voting: false,
  private: false,
  status: "draft",
  file_ids: [],
  tags: [],
  interestgroup_ids: [],
  users: [],
  interestgroup: [],
  pinned: 0,
};

export default {
  namespaced: true,
  state: {
    questions: [],
    error: {},
    responses: [],
    question: defaultState,
    filters: {
      searchQuery: "",
      interestgroups: [],
      tabFilter: "",
      size: null,
    },
    sortBy: {
      field: "created_time",
      direction: "desc",
    },
  },

  actions: {
    // TODO: These need to be order-able, so remember to add the filter in
    async list({ commit, rootState, state, dispatch }, options) {
      console.log(
        "🚀 ~ file: Questions.js ~ line 47 ~ list ~ options",
        options
      );
      let currentUserId;
      if (!rootState.auth.currentUser.id) {
        const resp = await dispatch("auth/listCurrentUser", {}, { root: true });
        currentUserId = resp.id;
      } else {
        currentUserId = rootState.auth.currentUser.id;
      }
      const filter = {
        sort: [
          {
            pinned: {
              order: "desc",
            },
          },
          {
            [state.sortBy.field]: {
              order: state.sortBy.direction,
            },
          },
        ],
        query: {
          bool: {
            must: [],
            should: [
              {
                bool: {
                  must: [
                    {
                      match: {
                        title: {
                          query: state.filters.searchQuery,
                          operator: "and",
                          zero_terms_query: "all",
                          fuzziness: "AUTO",
                        },
                      },
                    },
                  ],
                  must_not: [
                    {
                      term: {
                        private: 1,
                      },
                    },
                  ],
                },
              },
              {
                bool: {
                  must: [
                    {
                      match: {
                        title: {
                          query: state.filters.searchQuery,
                          operator: "and",
                          zero_terms_query: "all",
                          fuzziness: "AUTO",
                        },
                      },
                    },
                    {
                      term: {
                        private: 1,
                      },
                    },
                    {
                      terms: {
                        users: [currentUserId],
                      },
                    },
                  ],
                },
              },
            ],
            must_not: [],
          },
        },
      };

      if (state.filters.size) {
        filter.size = state.filters.size;
      } else {
        filter.size = 10000;
      }

      if (state.filters.interestgroups.length >= 1) {
        // should either be linked to specified groups or none at all 
        filter.query.bool.should[0].bool.must.push({
          bool: {
            should: [
              {
                bool: {
                  must: {
                    terms: {
                      interestgroups: state.filters.interestgroups,
                    },
                  },
                },
              },
              {
                bool: {
                  must_not: {
                    exists: {
                      field: "interestgroups",
                    },
                  },
                },
              },
            ],
          },
        });
      }

      if (state.filters.tabFilter === "my-questions") {
        filter.query.bool.must.push({
          bool: {
            should: [
              {
                term: {
                  created_user_id: currentUserId,
                },
              },
            ],
          },
        });
      } else if (!state.filters.tabFilter) {
        filter.query.bool.must_not = [
          {
            term: {
              status: "draft",
            },
          },
        ];
      }

      if (options && options.hideClosed) {
        filter.query.bool.must_not.push({
          term: {
            status: "closed",
          },
        });
        filter.query.bool.must_not.push({
          range: {
            end_date: {
              lte: new Date()
                .getTime()
                .toString()
                .slice(0, -3),
            },
          },
        });
      }

      let resp;

      try {
        resp = await api().put("/collaboration/questions/flat", { filter });
        // resp = await api().get('/collaboration/questions');

        // if we have a filter... filter!
        const questions = resp.data.data.question;

        commit("SET_QUESTIONS", questions);
        return Promise.resolve(questions);
      } catch (err) {
        if (err.response.status === 404 && !err.response.data.data) {
          commit("SET_QUESTIONS", []);
          return Promise.resolve([]);
        }
        commit("SET_ERROR", { ...err });
        commit("SET_QUESTIONS", []);
        return Promise.resolve([]);
      }
    },

    async publish({ commit }, questionId) {
      let resp;
      try {
        resp = await api().put(`/collaboration/question/${questionId}/publish`);
        return Promise.resolve(resp);
      } catch (err) {
        commit("SET_ERROR", { ...err });
      }
      return Promise.reject();
    },

    async read({ commit }, questionId) {
      let resp;
      try {
        resp = await api().get(`/collaboration/question/${questionId}`);
        const question = {
          ...resp.data.data.question[0],
          voting: !!resp.data.data.question[0].voting,
          private: !!resp.data.data.question[0].private,
        };
        commit("SET_QUESTION", question);
        return Promise.resolve(question);
      } catch (err) {
        commit("SET_ERROR", { ...err });
      }

      return Promise.reject();
    },
    updateValue({ commit }, payload) {
      commit("updateValue", payload);
    },

    async create(context, payload) {
      // Post the data.  Should have been validated at this point
      const data = { question: payload };
      let apiResp;
      try {
        apiResp = await api().post("/collaboration/question", data);
        // Should be a success here
        return Promise.resolve(apiResp);
      } catch (error) {
        return Promise.reject(error);
      }
    },

    async update(context, payload) {
      const data = { question: payload };
      let apiResp;
      try {
        apiResp = await api().post(
          `/collaboration/question/${payload.id}`,
          data
        );
        // Should be a success here
        return Promise.resolve(apiResp);
      } catch (error) {
        return Promise.reject(error);
      }
    },

    async invite(context, { questionId, userIds }) {
      let resp;
      try {
        resp = await api().post(
          `/collaboration/question/${questionId}/invite`,
          {
            invite: { user_ids: userIds },
          }
        );
        return Promise.resolve(resp);
      } catch (err) {
        console.log({ ...err });
        return Promise.reject(err);
      }
    },

    async follow(context, id) {
      let resp;
      try {
        resp = await api().put(`/collaboration/question/${id}/favourite`);
        return Promise.resolve(resp);
      } catch (err) {
        console.log({ ...err });
        return Promise.reject(err);
      }
    },

    async leave(context, id) {
      let resp;
      try {
        resp = await api().put(`/collaboration/question/${id}/leave`);
        return Promise.resolve(resp);
      } catch (err) {
        console.log({ ...err });
        return Promise.reject(err);
      }
    },

    async unfollow(context, id) {
      let resp;
      try {
        resp = await api().put(`/collaboration/question/${id}/unfavourite`);
        return Promise.resolve(resp);
      } catch (err) {
        console.log({ ...err });
        return Promise.reject(err);
      }
    },

    async close({ commit }, id) {
      let resp;
      try {
        resp = await api().put(`/collaboration/question/${id}/close`);
        commit("CLOSE_QUESTION");
        return Promise.resolve(resp);
      } catch (err) {
        console.log({ ...err });
        return Promise.reject(err);
      }
    },

    async delete({ dispatch }, id) {
      let resp;
      try {
        resp = await api().delete(`/collaboration/question/${id}`);
        dispatch("list");
        return Promise.resolve(resp.data.data.question);
      } catch (err) {
        return Promise.reject(err);
      }
    },

    async pin({ commit }, id) {
      let resp;
      try {
        resp = await api().put(`/collaboration/question/${id}/pin`);
        commit("UPDATE_PIN_STATUS", 1);
        return Promise.resolve(resp);
      } catch (err) {
        console.log({ ...err });
        return Promise.reject(err);
      }
    },
    async unpin({ commit }, id) {
      let resp;
      try {
        resp = await api().put(`/collaboration/question/${id}/unpin`);
        commit("UPDATE_PIN_STATUS", 0);
        return Promise.resolve(resp);
      } catch (err) {
        console.log({ ...err });
        return Promise.reject(err);
      }
    },

    async respond({ state, dispatch }, feedback) {
      const questionId = state.question.id;

      const payload = {
        response: { description: feedback, question_id: questionId },
      };

      let response;

      try {
        response = await api().post(
          `/collaboration/question/${questionId}/response`,
          payload
        );
        dispatch("listResponses", { questionId });
        return Promise.resolve(response);
      } catch (err) {
        console.log({ ...err });
        return Promise.reject(err);
      }
    },

    async listResponses({ commit }, params) {
      let response;

      const { questionId } = params;

      let endpoint = `/collaboration/question/${questionId}/responses`;

      if (params.isFlat) {
        endpoint = `${endpoint}/flat`;
      }

      try {
        response = await api().get(endpoint);
        commit("SET_RESPONSES", response.data.data.response);
        return Promise.resolve(response.data.data.response);
      } catch (err) {
        commit("SET_RESPONSES", []);
        return Promise.reject(err);
      }
    },

    getAllQuestions({ state, dispatch }) {
      state.filters.tabFilter = "";
      dispatch("list");
    },

    getInterestQuestions({ commit, dispatch, rootState }) {
      const interestGroups = rootState.auth.currentUser.interestgroups;
      if (interestGroups && interestGroups.length > 0) {
        const grpIds = interestGroups.map((grp) => grp.id);
        commit("setFilterCategories", grpIds);
        commit("setTabFilter", null);
        dispatch("list");
      } else {
        // If we are not part of any interest groups, return new questions
        // TODO: Or should we get the default list?
        commit("setFilterCategories", []);
        commit("setTabFilter", null);
        dispatch("list");
        // commit('SET_QUESTIONS',[])
      }
    },

    getMyQuestions({ dispatch, state }) {
      state.filters.tabFilter = "my-questions";
      dispatch("list");
    },

    resetState({ commit }) {
      commit("RESET_QUESTION");
    },
  },

  mutations: {
    UPDATE_PIN_STATUS(state, status) {
      // console.log(state);
      // console.log(status);
      state.question.pinned = status;
    },
    SET_QUESTIONS(state, payload) {
      state.questions = payload;
    },
    SET_QUESTION(state, payload) {
      state.question = payload;
    },
    SET_ERROR(state, payload) {
      state.error = payload;
    },
    RESET_QUESTION(state) {
      state.question = { ...defaultState };
    },
    CLOSE_QUESTION(state) {
      state.question.status = "closed";
    },
    SET_RESPONSES(state, resp) {
      state.responses = resp;
    },
    // not constant to match naming conventions used elsewhere
    setSearchQuery(state, payload) {
      state.filters.searchQuery = payload;
    },
    setTabFilter(state, tabFilter) {
      state.filters.tabFilter = tabFilter;
    },
    setFilterCategories(state, categories) {
      state.filters.interestgroups = categories;
    },
    setFilterSize(state, size) {
      state.filters.size = size;
    },
    setSortBy(state, { direction, field }) {
      // set direction to 'asc' if not set
      const localDirection = direction || "asc";

      state.sortBy.direction = localDirection;
      state.sortBy.field = ["created_time", "end_date"].includes(field)
        ? field
        : field.split("_")[1]; // hack
    },
  },

  getters: {
    /**
     * Split questions into open and closed
     */
    groupedQuestions: (state) => {
      // return state.questions;
      const open = state.questions.filter(
        (q) =>
          q.status !== "closed" &&
          dayjs(q.end_date).valueOf() > dayjs().valueOf()
      );
      const closed = state.questions.filter(
        (q) =>
          q.status === "closed" ||
          dayjs(q.end_date).valueOf() < dayjs().valueOf()
      );

      return { open, closed };
    },

    questionFeed: (state) => {
      const questions = [...state.questions];
      return questions;
    },
  },
};
